import { useState, useEffect } from 'react';
import { Users, Target, Brain, TrendingUp, BookOpen, BarChart3 } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { logger } from '../../services/logger';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  userGrowth: number;
  totalJournalEntries: number;
  totalCoachMessages: number;
  totalAnalyticsEvents: number;
}

const initialStats: DashboardStats = {
  totalUsers: 0,
  activeUsers: 0,
  userGrowth: 0,
  totalJournalEntries: 0,
  totalCoachMessages: 0,
  totalAnalyticsEvents: 0
};

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>(initialStats);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);
      setError(null);

      const { count: totalUsers, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { count: activeUsers, error: activeError } = await supabase
        .from('analytics_events')
        .select('user_id', { count: 'exact', head: true })
        .gte('timestamp', thirtyDaysAgo.toISOString());

      if (activeError && activeError.code !== 'PGRST116') {
        logger.warn('Error getting active users:', activeError);
      }

      const { count: totalJournalEntries, error: journalError } = await supabase
        .from('journal_entries')
        .select('*', { count: 'exact', head: true });

      if (journalError && journalError.code !== 'PGRST116') {
        logger.warn('Error getting journal entries:', journalError);
      }

      const { count: totalCoachMessages, error: coachError } = await supabase
        .from('coach_messages')
        .select('*', { count: 'exact', head: true });

      if (coachError && coachError.code !== 'PGRST116') {
        logger.warn('Error getting coach messages:', coachError);
      }

      const { count: totalAnalyticsEvents, error: analyticsError } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true });

      if (analyticsError && analyticsError.code !== 'PGRST116') {
        logger.warn('Error getting analytics events:', analyticsError);
      }

      const { count: newUsers, error: newUsersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (newUsersError) throw newUsersError;

      const userGrowth = totalUsers ? Math.round((newUsers || 0) / totalUsers * 100) : 0;

      const { data: users, error: usersListError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (usersListError) throw usersListError;

      setStats({
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        userGrowth,
        totalJournalEntries: totalJournalEntries || 0,
        totalCoachMessages: totalCoachMessages || 0,
        totalAnalyticsEvents: totalAnalyticsEvents || 0
      });

      if (users) {
        setRecentUsers(users);
      }
    } catch (err: any) {
      logger.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please check your database permissions.');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
          {error}
        </div>
        <button
          onClick={() => loadDashboardData()}
          className="px-4 py-2 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-ink-950">Admin Dashboard</h1>
        <p className="text-gray-700">Monitor platform performance and user activity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-gold-500/10">
              <Users className="h-6 w-6 text-gold-500" />
            </div>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
              stats.userGrowth > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {stats.userGrowth > 0 ? '+' : ''}{stats.userGrowth}%
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Total Users</p>
          <p className="text-2xl font-bold text-ink-950">{stats.totalUsers}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-success-500/10">
              <Target className="h-6 w-6 text-success-500" />
            </div>
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
              Active
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Active Users (30d)</p>
          <p className="text-2xl font-bold text-ink-950">{stats.activeUsers}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-electric-400/10">
              <Brain className="h-6 w-6 text-electric-400" />
            </div>
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-electric-400/20 text-electric-400">
              Total
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Coach Messages</p>
          <p className="text-2xl font-bold text-ink-950">{stats.totalCoachMessages}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-gold-500/10">
              <BookOpen className="h-6 w-6 text-gold-500" />
            </div>
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gold-500/20 text-gold-500">
              Total
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Journal Entries</p>
          <p className="text-2xl font-bold text-ink-950">{stats.totalJournalEntries}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-gold-500/10">
              <BarChart3 className="h-6 w-6 text-gold-500" />
            </div>
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gold-500/20 text-gold-500">
              Total
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Analytics Events</p>
          <p className="text-2xl font-bold text-ink-950">{stats.totalAnalyticsEvents}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-gold-500/10">
              <TrendingUp className="h-6 w-6 text-gold-500" />
            </div>
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gold-500/20 text-gold-500">
              Growth
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">User Growth (30d)</p>
          <p className="text-2xl font-bold text-ink-950">{stats.userGrowth}%</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-heading font-semibold text-ink-950 mb-6">Platform Activity</h2>
        <div className="grid gap-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gold-500/10 rounded-lg flex items-center justify-center mr-4">
              <Brain className="h-6 w-6 text-gold-500" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-ink-950">Coach Messages</span>
                <span className="text-sm text-gray-500">{stats.totalCoachMessages}</span>
              </div>
              <div className="h-2 bg-paper rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-500 rounded-full"
                  style={{ width: `${Math.min((stats.totalCoachMessages / 1000) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-12 h-12 bg-gold-500/10 rounded-lg flex items-center justify-center mr-4">
              <BookOpen className="h-6 w-6 text-gold-500" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-ink-950">Journal Entries</span>
                <span className="text-sm text-gray-500">{stats.totalJournalEntries}</span>
              </div>
              <div className="h-2 bg-paper rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-500 rounded-full"
                  style={{ width: `${Math.min((stats.totalJournalEntries / 1000) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-12 h-12 bg-gold-500/10 rounded-lg flex items-center justify-center mr-4">
              <BarChart3 className="h-6 w-6 text-gold-500" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-ink-950">Analytics Events</span>
                <span className="text-sm text-gray-500">{stats.totalAnalyticsEvents}</span>
              </div>
              <div className="h-2 bg-paper rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-500 rounded-full"
                  style={{ width: `${Math.min((stats.totalAnalyticsEvents / 5000) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-heading font-semibold text-ink-950">Recent Users</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-paper">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentUsers.map((user) => (
                <tr key={user.id} className="hover:bg-paper transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gold-500/10 flex items-center justify-center">
                        <span className="text-gold-500 font-medium">
                          {user.full_name?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-ink-950">
                          {user.full_name || user.email}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-ink-950">{user.subscription_tier || 'free'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.subscription_status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.subscription_status || 'inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
